import React, { useMemo } from "react";
import { ListBase, ListProps, useDataProvider, useNotify, usePermissions } from "react-admin";
import { Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import { FilterField } from "@src/components/Common/DataList";
import { NetOrcaTableListV1, TableListColumn } from "@src/components/Common/NetOrcaTableList";
import { ExtendedDataProvider } from "@src/types";
import PackDashboardTabs from "../Pack/PackDashboard/PackDashboardTabs";
import LLMModelRow, { PROVIDER_LABELS } from "./LLMModelRow";
import "./LLMModels.css";

const resource = "llm_models";

/**
 * The providers worth filtering by for now. A model can be any of the ten the
 * create form offers and the rows name whichever it is — this is only the
 * shortlist the filter offers to narrow by.
 */
const FILTERABLE_PROVIDERS = ['anthropic', 'openai', 'gemini', 'xai', 'genai', 'custom'];

const providerOptions = FILTERABLE_PROVIDERS
    .map((value) => ({ value, label: PROVIDER_LABELS[value] || value }));

const LLMModelsListView = () => {
    const { permissions } = usePermissions();
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();

    // The model is stored, so the id alone is enough to test it — no body, and
    // nothing here has to know how it is configured.
    const testConnection = (record: any) => dataProvider
        .createAction(resource, { data: undefined, verb: `test_connection/${record.id}/` })
        .then(() => notify(`${record.name}: connection test successful`, 'success'))
        .catch((error: any) => {
            const message = error.body?.error?.error?.message || error.body?.message || error.message || 'Unknown error';
            notify(`${record.name}: ${message}`, 'error');
        });

    // A model has two names — the one it was given here and the one its provider
    // knows it by — so each column searches its own.
    const filterFields: FilterField[] = useMemo(() => [
        { name: 'name', label: 'Name', type: 'search', dataCy: 'name-search' },
        { name: 'model_name', label: 'Model', type: 'search', dataCy: 'model-name-search' },
        {
            name: 'provider',
            label: 'Provider',
            type: 'select',
            multiple: true,
            options: providerOptions,
            dataCy: 'provider-filter',
        },
    ], []);

    const columns: TableListColumn[] = useMemo(() => [
        { label: 'Name', filters: ['name'], dataCy: 'column-name' },
        { label: 'Model', filters: ['model_name'], dataCy: 'column-model' },
        { label: 'Provider', filters: ['provider'], dataCy: 'column-provider' },
        // the last track is the create button's, over each row's test button
    ], []);

    return (
        <NetOrcaTableListV1
            className="netorca-llm"
            dataCy="llm-model-result-list"
            fields={filterFields}
            columns={columns}
            template="var(--llm-cols)"
            banner={<PackDashboardTabs />}
            emptyText="No LLM models found"
            noun="model"
            headerTrailing={permissions === "ADMIN" && (
                <Link to={`/${resource}/create`}>
                    <Button className="llm-create" type="primary" size="small" icon={<PlusOutlined />}>
                        Add LLM model
                    </Button>
                </Link>
            )}
            renderRow={(record) => (
                <LLMModelRow
                    key={record.id}
                    record={record}
                    detailPath={`/${resource}`}
                    onTest={testConnection}
                />
            )}
        />
    );
};

const LLMModelsList = (props: ListProps) => (
    // page size is the batch the infinite scroll fetches
    <ListBase perPage={25} {...props}>
        <LLMModelsListView />
    </ListBase>
);

export default LLMModelsList;
